import type { ReadInputBarcodeFormat } from 'zxing-wasm/reader'
import type { BarcodeFormat } from '@/lib/api-types'

/**
 * Mapping between our stored format names and ZXing's.
 *
 * Kept explicit rather than derived: ZXing's names are its own vocabulary
 * ("EAN13", not "ean_13"), and a silent mismatch here would mean a workflow
 * quietly decodes nothing at all.
 *
 * Typed as ZXing's own union rather than `string`, so a typo is a compile
 * error instead of a format the decoder silently ignores at runtime.
 */
const TO_ZXING: Record<BarcodeFormat, ReadInputBarcodeFormat> = {
  qr_code: 'QRCode',
  data_matrix: 'DataMatrix',
  code_128: 'Code128',
  code_39: 'Code39',
  ean_13: 'EAN13',
}

const FROM_ZXING: Record<string, BarcodeFormat> = {
  QRCode: 'qr_code',
  MicroQRCode: 'qr_code',
  QRCodeModel1: 'qr_code',
  QRCodeModel2: 'qr_code',
  RMQRCode: 'qr_code',
  DataMatrix: 'data_matrix',
  Code128: 'code_128',
  Code39: 'code_39',
  Code39Std: 'code_39',
  Code39Ext: 'code_39',
  EAN13: 'ean_13',
}

/**
 * Format families.
 *
 * This split is the whole reason scanning gets faster: 2D symbols are found in
 * a square region near the centre, 1D symbols in a wide, short band. Running
 * one compromise crop for both means every frame is the wrong shape for one of
 * them.
 */
export type FormatFamily = '2d' | '1d'

const FAMILY: Record<BarcodeFormat, FormatFamily> = {
  qr_code: '2d',
  data_matrix: '2d',
  code_128: '1d',
  code_39: '1d',
  ean_13: '1d',
}

export function toZXingFormats(formats: readonly BarcodeFormat[]): ReadInputBarcodeFormat[] {
  return formats.map((f) => TO_ZXING[f])
}

export function fromZXingFormat(format: string): BarcodeFormat | null {
  return FROM_ZXING[format] ?? null
}

export function familyOf(format: BarcodeFormat): FormatFamily {
  return FAMILY[format]
}

/**
 * Split a workflow's enabled formats into the families that actually need to
 * run. A QR-only workflow returns just ['2d'] and therefore skips the 1D pass
 * entirely — roughly halving the per-frame work.
 */
export function familiesFor(formats: readonly BarcodeFormat[]): FormatFamily[] {
  const families: FormatFamily[] = []
  for (const f of formats) {
    const family = FAMILY[f]
    if (!families.includes(family)) families.push(family)
  }
  // Deterministic order: 2D first. QR and Data Matrix are what this app scans
  // most, so finding them first means the 1D pass usually never runs.
  return families.sort((a) => (a === '2d' ? -1 : 1))
}

export function formatsInFamily(
  formats: readonly BarcodeFormat[],
  family: FormatFamily,
): BarcodeFormat[] {
  return formats.filter((f) => FAMILY[f] === family)
}
